// Exportación de hitos a un calendario iCalendar (.ics, RFC 5545) para Outlook.
//
// Trabajo determinista: los mismos hitos dan siempre el mismo fichero (salvo la marca
// de tiempo DTSTAMP, que se inyecta). Cada hito es un evento de día completo que no
// ocupa agenda (TRANSP:TRANSPARENT, libre en Outlook) y lleva un UID estable por obra
// y tipo de hito, para que volver a importar actualice el evento en vez de duplicarlo
// en los calendarios que respetan el UID. SEQUENCE crece con cada exportación (minutos
// desde 1970) para que Outlook aplique una fecha movida en vez de ignorarla.

const CRLF = "\r\n";
const UID_DOMAIN = "book.grupoatlante.com";

// Texto de una propiedad TEXT: se escapan \ ; , y los saltos de línea.
export function escapeIcsText(value) {
    return String(value ?? "")
        .replace(/\\/g, "\\\\")
        .replace(/;/g, "\\;")
        .replace(/,/g, "\\,")
        .replace(/\r\n|\r|\n/g, "\\n");
}

// Plegado de líneas: como mucho 75 octetos (UTF-8) por línea; las siguientes empiezan
// con un espacio. No se parte nunca un carácter multibyte.
const utf8Length = (char) => {
    const code = char.codePointAt(0);
    if (code < 0x80) return 1;
    if (code < 0x800) return 2;
    return code < 0x10000 ? 3 : 4;
};

export function foldIcsLine(line) {
    const parts = [];
    let current = "";
    let bytes = 0;
    let limit = 75;
    for (const char of line) {
        const size = utf8Length(char);
        if (bytes + size > limit) {
            parts.push(current);
            current = "";
            bytes = 0;
            limit = 74; // la continuación ya lleva el espacio inicial
        }
        current += char;
        bytes += size;
    }
    parts.push(current);
    return parts.join(`${CRLF} `);
}

const compactDate = (iso) => iso.replace(/-/g, "");
const nextDayIso = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(Date.UTC(y, m - 1, d + 1));
    return date.toISOString().slice(0, 10);
};
const utcStamp = (date) => date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

// Nombre de fichero seguro (Windows) a partir de un texto.
export function icsFileName(base, date = new Date()) {
    const safe = String(base || "Obras").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "") || "Obras";
    const stamp = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    return `${safe}_${stamp}.ics`;
}

// Un evento de la agenda -> líneas VEVENT. `event`: { date, type: { id, prefix, label }, obra, hito? }.
function eventLines(event, stamp, sequence) {
    const { obra, type, hito } = event;
    const nombre = [obra.codigoTienda, obra.tienda].filter(Boolean).join(" ");
    const lugar = [obra.ciudad, obra.pais].filter(Boolean).join(", ");
    const detalle = [
        `Obra: ${nombre}`,
        lugar ? `Ubicación: ${lugar}` : "",
        obra.pm ? `PM: ${obra.pm}` : "",
        `Hito: ${type.label}`,
        hito ? `Situación: ${hito.statusLabel}${hito.realDate ? ` (confirmado el ${hito.realDate})` : ""}` : "",
        hito?.notes ? `Nota: ${hito.notes}` : "",
        "Generado desde Bershka Book · Obras",
    ].filter(Boolean).join("\n");
    return [
        "BEGIN:VEVENT",
        `UID:obra-${obra.codObra}-${type.id}@${UID_DOMAIN}`,
        `DTSTAMP:${stamp}`,
        `SEQUENCE:${sequence}`,
        `DTSTART;VALUE=DATE:${compactDate(event.date)}`,
        `DTEND;VALUE=DATE:${compactDate(nextDayIso(event.date))}`,
        `SUMMARY:${escapeIcsText(`[${type.prefix}] ${nombre} · ${type.label}`)}`,
        `DESCRIPTION:${escapeIcsText(detalle)}`,
        lugar ? `LOCATION:${escapeIcsText(lugar)}` : "",
        `CATEGORIES:${escapeIcsText("Obras BSK")},${escapeIcsText(type.label)}`,
        "TRANSP:TRANSPARENT",
        "X-MICROSOFT-CDO-BUSYSTATUS:FREE",
        "END:VEVENT",
    ].filter(Boolean);
}

// Calendario completo. `now` se inyecta para que las pruebas sean deterministas.
export function buildIcsCalendar(events = [], { name = "Obras BSK · hitos", now = new Date() } = {}) {
    const stamp = utcStamp(now);
    const sequence = Math.floor(now.getTime() / 60000);
    const lines = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Grupo Atlante//Bershka Book Obras//ES",
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        `X-WR-CALNAME:${escapeIcsText(name)}`,
        ...events.flatMap((event) => eventLines(event, stamp, sequence)),
        "END:VCALENDAR",
    ];
    return lines.map(foldIcsLine).join(CRLF) + CRLF;
}

// Descarga en el navegador (fuera de las pruebas).
export function downloadIcs(fileName, text) {
    const blob = new Blob([text], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
